/**
 * Turns query string like hello=1&another=2 into object {hello: 1, another: 2}
 * @param {*} queryString
 */
function parseQuery(queryString) {
  var query = {};
  var pairs = (
    queryString[0] === "?" ? queryString.substring(1) : queryString
  ).split("&");
  for (var i = 0; i < pairs.length; i++) {
    var pair = pairs[i].split("=");
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
  }
  return query;
}

function modifyColor(col, amt) {
  var usePound = false;

  if (col[0] == "#") {
    col = col.slice(1);
    usePound = true;
  }

  var num = parseInt(col, 16);

  var r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  var b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  var g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (
    (usePound ? "#" : "") +
    String("000000" + (g | (b << 8) | (r << 16)).toString(16)).slice(-6)
  );
}

function addAlpha(color, opacity) {
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

/** Utils for taxes calculations */
function calculatePercentage(percentage, value) {
  return (value * percentage) / 100;
}

async function getSHA256Hash(input) {
  const textAsBuffer = new TextEncoder().encode(input);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
  return hash;
}

function rgbToHex(rgbString) {
  const nums = rgbString
    .replace(/rgba?\\(|\\)|\\s+/g, "")
    .split(",")
    .slice(0, 3);

  if (nums.length < 3) return null;

  const [r, g, b] = nums.map((n) =>
    Math.max(0, Math.min(255, parseInt(n, 10)))
  );

  return [r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("");
}

function darkenColor(hex) {
  const num = parseInt(hex, 16);
  const r = Math.max(0, ((num >> 16) & 0xff) * 0.8);
  const g = Math.max(0, ((num >> 8) & 0xff) * 0.8);
  const b = Math.max(0, (num & 0xff) * 0.8);
  return (
    "#" +
    [r, g, b].map((n) => Math.round(n).toString(16).padStart(2, "0")).join("")
  );
}

const colorMappings = {
  // dark color → light replacement
  "#2e2e2e": "#f5f5f5",
  "#333333": "#f5f5f5",
  "#000000": "#f5f5f5",
  "#090909": "#f5f5f5",
  "#000": "#f5f5f5",
  "#333": "#f5f5f5",
  "#1e1e1e": "#f5f5f5",
  "#0e0e0e": "#f5f5f5",
  "#121212": "#f5f5f5",
  "#606060": "#f5f5f5",

  // light color → dark replacement
  "#ffffff": "#2a2a2a",
  "#fff": "#2a2a2a",
  "#fefefe": "#2a2a2a",
  "#f6f6f6": "#2a2a2a",
  "#f2f2f2": "#919191",
  "#f5f5f5": "#000",

  //other
  "#ff6900": "#ff6900",
  "#f25552d": "#f25552d",
};

function checkDarkMode(color, theme) {
  if (!color || !theme?.isDarkMode) return color;

  if (!color.startsWith("#")) {
    const hex = rgbToHex(color);
    if (hex) color = `#${hex}`;
  }

  const lower = color.toLowerCase();

  if (colorMappings[lower]) {
    return colorMappings[lower];
  }

  const hex = lower.replace("#", "");
  if (hex.length === 3) {
    const expanded = hex
      .split("")
      .map((c) => c + c)
      .join("");
    return darkenColor(expanded);
  }
  if (hex.length === 6) {
    return darkenColor(hex);
  }

  return color;
}

function checkCustomCSS(customCSS, theme) {
  if (!customCSS || !theme?.isDarkMode) return customCSS;
  let convertedCSS = customCSS;

  convertedCSS = convertedCSS.replace(/#([0-9A-F]{6}|[0-9A-F]{3})/gi, (match) =>
    checkDarkMode(match, theme)
  );

  convertedCSS = convertedCSS.replace(/rgba?\\([^\\)]+\\)/gi, (match) =>
    checkDarkMode(match, theme)
  );

  return convertedCSS;
}

/**
 * Returns the anchor (HTML id) of a Gutenberg block.
 * WPGraphQL only exposes `attributes.anchor` for blocks saved with the current
 * editor. Older blocks return null even though their rendered HTML carries the
 * id, so fall back to the id on the root element of the rendered HTML.
 * @param {*} block
 */
function getBlockAnchor(block) {
  const anchor = block?.attributes?.anchor;
  if (anchor) {
    return anchor;
  }

  const match =
    /^\s*<[a-z][a-z0-9]*\b[^>]*?\sid\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(
      block?.renderedHtml || ""
    );
  return match ? (match[1] ?? match[2]) : undefined;
}

module.exports = {
  getBlockAnchor,
  parseQuery,
  addAlpha,
  modifyColor,
  calculatePercentage,
  getSHA256Hash,
  checkDarkMode,
  checkCustomCSS,
};
